/**
 * Content Hub Settings Page
 * Admin-only configuration page for Content Hub settings.
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Settings,
  FolderTree,
  Tags,
  FileType,
  Database,
  Shield,
  Download,
  Upload,
  Save,
  RefreshCw,
  Trash2,
  Plus,
  Play,
  CheckCircle2,
  XCircle,
  Loader2,
  Grid,
  List,
  Merge,
  Clock,
} from 'lucide-react'
import { PageHeader, FluentCard } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { PermissionGate } from '@/components/auth/permission-gate'
import { useToast } from '@/components/ui/use-toast'
import type {
  ContentHubGeneralSettings,
  ContentHubTagSettings,
  ContentHubConnectorSettings,
  ContentHubPermissionSettings,
  ContentHubImportExportSettings,
  DataConnectorItem,
} from '@/lib/api/services/contentHubSettings'

// ============================================
// Types
// ============================================

type SettingsTab =
  | 'general'
  | 'folders'
  | 'tags'
  | 'content-types'
  | 'connectors'
  | 'permissions'
  | 'import-export'

// ============================================
// Mock Data (for UI development - remove when API is ready)
// ============================================

const mockGeneralSettings: ContentHubGeneralSettings = {
  defaultView: 'grid',
  itemsPerPage: 24,
  enabledContentTypes: [
    { type: 'TextElement', enabled: true, maxFileSizeMb: 1, allowedFormats: [], requiredFields: [], defaultMetadata: {} },
    { type: 'DataPoint', enabled: true, maxFileSizeMb: 0.1, allowedFormats: [], requiredFields: [], defaultMetadata: {} },
    { type: 'ImageElement', enabled: true, maxFileSizeMb: 10, allowedFormats: ['jpg', 'png', 'svg', 'webp'], requiredFields: [], defaultMetadata: {} },
    { type: 'TableElement', enabled: true, maxFileSizeMb: 5, allowedFormats: [], requiredFields: [], defaultMetadata: {} },
    { type: 'EmailSnippet', enabled: true, maxFileSizeMb: 1, allowedFormats: [], requiredFields: [], defaultMetadata: {} },
    { type: 'SlideElement', enabled: true, maxFileSizeMb: 50, allowedFormats: ['pptx', 'ppt'], requiredFields: [], defaultMetadata: {} },
  ],
  autoTaggingEnabled: true,
  usageTrackingEnabled: true,
  maxStorageMb: 5120,
  retentionDays: null,
}

const mockTagSettings: ContentHubTagSettings = {
  tags: [
    { id: '1', name: 'Marketing', usageCount: 45, createdAt: '2024-01-15', lastUsedAt: '2024-01-20' },
    { id: '2', name: 'Legal', usageCount: 32, createdAt: '2024-01-10', lastUsedAt: '2024-01-19' },
    { id: '3', name: 'Sales', usageCount: 28, createdAt: '2024-01-12', lastUsedAt: '2024-01-20' },
    { id: '4', name: 'Branding', usageCount: 22, createdAt: '2024-01-08', lastUsedAt: '2024-01-18' },
    { id: '5', name: 'Template', usageCount: 18, createdAt: '2024-01-05', lastUsedAt: '2024-01-17' },
    { id: '6', name: 'Archived', usageCount: 0, createdAt: '2024-01-01', lastUsedAt: undefined },
  ],
  tagGroups: [
    { id: '1', name: 'Department', color: '#3b82f6', tagCount: 5 },
    { id: '2', name: 'Status', color: '#10b981', tagCount: 3 },
  ],
  autoSuggestEnabled: true,
  maxTagsPerItem: 10,
  allowCustomTags: true,
}

const mockConnectorSettings: ContentHubConnectorSettings = {
  connectors: [
    { id: '1', name: 'User Profile', type: 'UserProfile', status: 'active', lastSyncAt: '2024-01-20T10:30:00Z', lastSyncStatus: 'Success', fieldCount: 10 },
    { id: '2', name: 'Company Info', type: 'TenantInfo', status: 'active', lastSyncAt: '2024-01-20T10:30:00Z', lastSyncStatus: 'Success', fieldCount: 8 },
    { id: '3', name: 'Salesforce CRM', type: 'CRM', status: 'inactive', fieldCount: 15, syncScheduleMinutes: 60 },
    { id: '4', name: 'SharePoint Docs', type: 'SharePoint', status: 'error', lastSyncAt: '2024-01-19T15:00:00Z', lastSyncStatus: 'Connection failed', fieldCount: 5 },
  ],
  syncSchedule: {
    defaultIntervalMinutes: 60,
    enabledByDefault: false,
    maxConcurrentSyncs: 3,
  },
}

const mockPermissionSettings: ContentHubPermissionSettings = {
  canCreateContent: [
    { type: 'role', id: 'admin', name: 'Admin' },
    { type: 'role', id: 'editor', name: 'Editor' },
  ],
  canDeleteContent: [
    { type: 'role', id: 'admin', name: 'Admin' },
  ],
  canManageFolders: [
    { type: 'role', id: 'admin', name: 'Admin' },
  ],
  canManageTags: [
    { type: 'role', id: 'admin', name: 'Admin' },
    { type: 'role', id: 'editor', name: 'Editor' },
  ],
  approvalWorkflowEnabled: false,
  approvalWorkflowId: undefined,
}

const mockImportExportSettings: ContentHubImportExportSettings = {
  lastBackupAt: '2024-01-19T02:00:00Z',
  autoBackupEnabled: true,
  autoBackupIntervalDays: 7,
  exportFormats: ['csv', 'json', 'zip'],
  importFormats: ['csv', 'json', 'zip'],
}

// ============================================
// Component
// ============================================

export default function ContentHubSettings() {
  const { toast } = useToast()
  const [activeTab, setActiveTab] = useState<SettingsTab>('general')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Settings state
  const [generalSettings, setGeneralSettings] = useState<ContentHubGeneralSettings>(mockGeneralSettings)
  const [tagSettings, setTagSettings] = useState<ContentHubTagSettings>(mockTagSettings)
  const [connectorSettings, setConnectorSettings] = useState<ContentHubConnectorSettings>(mockConnectorSettings)
  const [permissionSettings, setPermissionSettings] = useState<ContentHubPermissionSettings>(mockPermissionSettings)
  const [importExportSettings, setImportExportSettings] = useState<ContentHubImportExportSettings>(mockImportExportSettings)

  // Dialog states
  const [showMergeTagsDialog, setShowMergeTagsDialog] = useState(false)
  const [showDeleteUnusedTagsDialog, setShowDeleteUnusedTagsDialog] = useState(false)
  const [showCreateTagGroupDialog, setShowCreateTagGroupDialog] = useState(false)
  const [showTestConnectorDialog, setShowTestConnectorDialog] = useState(false)
  const [selectedConnector, setSelectedConnector] = useState<DataConnectorItem | null>(null)
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [newTagGroupName, setNewTagGroupName] = useState('')
  const [testingConnector, setTestingConnector] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)

  // Load settings on mount
  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = useCallback(async () => {
    setLoading(true)
    try {
      // TODO: Replace with actual API calls when backend is ready
      // const settings = await contentHubSettingsService.getAllSettings()
      // setGeneralSettings(settings.general)
      // setTagSettings(settings.tags)
      // setConnectorSettings(settings.connectors)
      // setPermissionSettings(settings.permissions)
      // setImportExportSettings(settings.importExport)

      // Using mock data for now
      await new Promise(resolve => setTimeout(resolve, 500))
      setGeneralSettings(mockGeneralSettings)
      setTagSettings(mockTagSettings)
      setConnectorSettings(mockConnectorSettings)
      setPermissionSettings(mockPermissionSettings)
      setImportExportSettings(mockImportExportSettings)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load settings',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [toast])

  // ----------------------------------------
  // Save Handlers
  // ----------------------------------------

  const handleSaveGeneralSettings = async () => {
    setSaving(true)
    try {
      // TODO: await contentHubSettingsService.updateGeneralSettings(generalSettings)
      await new Promise(resolve => setTimeout(resolve, 500))
      toast({
        title: 'Success',
        description: 'General settings saved successfully',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save general settings',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleSaveTagSettings = async () => {
    setSaving(true)
    try {
      // TODO: await contentHubSettingsService.updateTagSettings(tagSettings)
      await new Promise(resolve => setTimeout(resolve, 500))
      toast({
        title: 'Success',
        description: 'Tag settings saved successfully',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save tag settings',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleSavePermissionSettings = async () => {
    setSaving(true)
    try {
      // TODO: await contentHubSettingsService.updatePermissionSettings(permissionSettings)
      await new Promise(resolve => setTimeout(resolve, 500))
      toast({
        title: 'Success',
        description: 'Permission settings saved successfully',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save permission settings',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleSaveImportExportSettings = async () => {
    setSaving(true)
    try {
      // TODO: await contentHubSettingsService.updateImportExportSettings(importExportSettings)
      await new Promise(resolve => setTimeout(resolve, 500))
      toast({
        title: 'Success',
        description: 'Import/Export settings saved successfully',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  // ----------------------------------------
  // Tag Actions
  // ----------------------------------------

  const handleMergeTags = async () => {
    if (selectedTags.length < 2) {
      toast({
        title: 'Error',
        description: 'Select at least 2 tags to merge',
        variant: 'destructive',
      })
      return
    }

    setSaving(true)
    try {
      // TODO: await contentHubSettingsService.mergeTags({ sourceTagIds: selectedTags, targetTagName: selectedTags[0] })
      await new Promise(resolve => setTimeout(resolve, 500))
      toast({
        title: 'Success',
        description: `Merged ${selectedTags.length} tags`,
      })
      setShowMergeTagsDialog(false)
      setSelectedTags([])
      await loadSettings()
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to merge tags',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteUnusedTags = async () => {
    setSaving(true)
    try {
      // TODO: const result = await contentHubSettingsService.deleteUnusedTags()
      await new Promise(resolve => setTimeout(resolve, 500))
      const unusedCount = tagSettings.tags.filter(t => t.usageCount === 0).length
      toast({
        title: 'Success',
        description: `Deleted ${unusedCount} unused tags`,
      })
      setShowDeleteUnusedTagsDialog(false)
      await loadSettings()
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete unused tags',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleCreateTagGroup = async () => {
    if (!newTagGroupName.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a group name',
        variant: 'destructive',
      })
      return
    }

    setSaving(true)
    try {
      // TODO: await contentHubSettingsService.createTagGroup({ name: newTagGroupName })
      await new Promise(resolve => setTimeout(resolve, 500))
      toast({
        title: 'Success',
        description: `Created tag group "${newTagGroupName}"`,
      })
      setShowCreateTagGroupDialog(false)
      setNewTagGroupName('')
      await loadSettings()
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create tag group',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  // ----------------------------------------
  // Connector Actions
  // ----------------------------------------

  const handleTestConnector = async (connector: DataConnectorItem) => {
    setSelectedConnector(connector)
    setShowTestConnectorDialog(true)
    setTestingConnector(true)
    setTestResult(null)

    try {
      // TODO: const result = await contentHubSettingsService.testConnector(connector.id)
      await new Promise(resolve => setTimeout(resolve, 1500))
      const success = connector.status !== 'error'
      setTestResult({
        success,
        message: success ? 'Connection successful' : 'Connection failed: Invalid credentials',
      })
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Connection test failed',
      })
    } finally {
      setTestingConnector(false)
    }
  }

  const handleSyncConnector = async (connector: DataConnectorItem) => {
    toast({
      title: 'Sync Started',
      description: `Syncing ${connector.name}...`,
    })

    try {
      // TODO: await contentHubSettingsService.syncConnector(connector.id)
      await new Promise(resolve => setTimeout(resolve, 2000))
      toast({
        title: 'Sync Complete',
        description: `${connector.name} synced successfully`,
      })
      await loadSettings()
    } catch (error) {
      toast({
        title: 'Sync Failed',
        description: `Failed to sync ${connector.name}`,
        variant: 'destructive',
      })
    }
  }

  // ----------------------------------------
  // Import/Export Actions
  // ----------------------------------------

  const handleExport = async (format: 'csv' | 'json' | 'zip') => {
    toast({
      title: 'Export Started',
      description: `Preparing ${format.toUpperCase()} export...`,
    })

    try {
      // TODO: const blob = await contentHubSettingsService.exportContent(format)
      await new Promise(resolve => setTimeout(resolve, 1500))
      // Download file
      // const url = URL.createObjectURL(blob)
      // const a = document.createElement('a')
      // a.href = url
      // a.download = `content-hub-export.${format}`
      // a.click()
      // URL.revokeObjectURL(url)
      toast({
        title: 'Export Complete',
        description: `Content exported as ${format.toUpperCase()}`,
      })
    } catch (error) {
      toast({
        title: 'Export Failed',
        description: 'Failed to export content',
        variant: 'destructive',
      })
    }
  }

  const handleCreateBackup = async () => {
    toast({
      title: 'Backup Started',
      description: 'Creating backup...',
    })

    try {
      // TODO: await contentHubSettingsService.createBackup()
      await new Promise(resolve => setTimeout(resolve, 2000))
      toast({
        title: 'Backup Complete',
        description: 'Backup created successfully',
      })
      await loadSettings()
    } catch (error) {
      toast({
        title: 'Backup Failed',
        description: 'Failed to create backup',
        variant: 'destructive',
      })
    }
  }

  // ----------------------------------------
  // Render Helpers
  // ----------------------------------------

  const getConnectorStatusBadge = (status: DataConnectorItem['status']) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Active</Badge>
      case 'inactive':
        return <Badge variant="secondary">Inactive</Badge>
      case 'error':
        return <Badge variant="destructive">Error</Badge>
    }
  }

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return 'Never'
    return new Date(dateStr).toLocaleString()
  }

  // ----------------------------------------
  // Render
  // ----------------------------------------

  // Permission check wrapper - wraps entire component including loading state
  return (
    <PermissionGate permissions="content.admin" fallback={
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <Shield className="h-12 w-12 text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Access Denied</h2>
        <p className="text-gray-500 mt-2">You don't have permission to access Content Hub settings.</p>
      </div>
    }>
      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : (
      <div className="space-y-6">
        <PageHeader
          title="Content Hub Settings"
          description="Configure Content Hub behavior, permissions, and integrations"
          breadcrumbs={[
            { label: 'Dashboard', href: '/' },
            { label: 'Content Hub', href: '/content' },
            { label: 'Settings' },
          ]}
        />

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as SettingsTab)} className="space-y-6">
          <TabsList className="grid w-full grid-cols-7 gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl">
            <TabsTrigger value="general" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900">
              <Settings className="h-4 w-4 mr-2" />
              General
            </TabsTrigger>
            <TabsTrigger value="folders" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900">
              <FolderTree className="h-4 w-4 mr-2" />
              Folders
            </TabsTrigger>
            <TabsTrigger value="tags" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900">
              <Tags className="h-4 w-4 mr-2" />
              Tags
            </TabsTrigger>
            <TabsTrigger value="content-types" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900">
              <FileType className="h-4 w-4 mr-2" />
              Types
            </TabsTrigger>
            <TabsTrigger value="connectors" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900">
              <Database className="h-4 w-4 mr-2" />
              Connectors
            </TabsTrigger>
            <TabsTrigger value="permissions" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900">
              <Shield className="h-4 w-4 mr-2" />
              Permissions
            </TabsTrigger>
            <TabsTrigger value="import-export" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900">
              <Download className="h-4 w-4 mr-2" />
              Import/Export
            </TabsTrigger>
          </TabsList>

          {/* General Settings Tab */}
          <TabsContent value="general" className="space-y-6">
            <FluentCard>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
                  <Settings className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">General Settings</h3>
                  <p className="text-sm text-gray-500">Configure default behavior and display options</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Default View */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                  <div className="space-y-0.5">
                    <Label>Default View</Label>
                    <p className="text-sm text-gray-500">Choose the default layout for content browsing</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant={generalSettings.defaultView === 'grid' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setGeneralSettings(s => ({ ...s, defaultView: 'grid' }))}
                    >
                      <Grid className="h-4 w-4 mr-1" />
                      Grid
                    </Button>
                    <Button
                      variant={generalSettings.defaultView === 'list' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setGeneralSettings(s => ({ ...s, defaultView: 'list' }))}
                    >
                      <List className="h-4 w-4 mr-1" />
                      List
                    </Button>
                  </div>
                </div>

                {/* Items per Page */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                  <div className="space-y-0.5">
                    <Label>Items per Page</Label>
                    <p className="text-sm text-gray-500">Number of items to display per page</p>
                  </div>
                  <Select
                    value={String(generalSettings.itemsPerPage)}
                    onValueChange={(v) => setGeneralSettings(s => ({ ...s, itemsPerPage: Number(v) }))}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="12">12</SelectItem>
                      <SelectItem value="24">24</SelectItem>
                      <SelectItem value="48">48</SelectItem>
                      <SelectItem value="96">96</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Auto-Tagging */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                  <div className="space-y-0.5">
                    <Label>AI Auto-Tagging</Label>
                    <p className="text-sm text-gray-500">Automatically suggest tags using AI when uploading content</p>
                  </div>
                  <Switch
                    checked={generalSettings.autoTaggingEnabled}
                    onCheckedChange={(checked) => setGeneralSettings(s => ({ ...s, autoTaggingEnabled: checked }))}
                  />
                </div>

                {/* Usage Tracking */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                  <div className="space-y-0.5">
                    <Label>Usage Tracking</Label>
                    <p className="text-sm text-gray-500">Track how often content items are used</p>
                  </div>
                  <Switch
                    checked={generalSettings.usageTrackingEnabled}
                    onCheckedChange={(checked) => setGeneralSettings(s => ({ ...s, usageTrackingEnabled: checked }))}
                  />
                </div>

                {/* Storage Limit */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                  <div className="space-y-0.5">
                    <Label>Storage Limit</Label>
                    <p className="text-sm text-gray-500">Maximum storage for Content Hub</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={generalSettings.maxStorageMb}
                      onChange={(e) => setGeneralSettings(s => ({ ...s, maxStorageMb: Number(e.target.value) }))}
                      className="w-24 text-right"
                    />
                    <span className="text-sm text-gray-500">MB</span>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button onClick={handleSaveGeneralSettings} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </div>
            </FluentCard>
          </TabsContent>

          {/* Folders Tab */}
          <TabsContent value="folders" className="space-y-6">
            <FluentCard>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600">
                  <FolderTree className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Folder Management</h3>
                  <p className="text-sm text-gray-500">Manage root folders and folder settings</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-gray-500">Root folders help organize your content library</span>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Root Folder
                  </Button>
                </div>

                <div className="border rounded-xl divide-y dark:divide-gray-700">
                  {['Company Information', 'Legal & Compliance', 'Marketing', 'Sales', 'Brand Assets'].map((folder, i) => (
                    <div key={i} className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                          <FolderTree className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <p className="font-medium">{folder}</p>
                          <p className="text-sm text-gray-500">{Math.floor(Math.random() * 50 + 10)} items</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm">Edit</Button>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between items-center pt-4 border-t">
                  <Button variant="outline">
                    <Upload className="h-4 w-4 mr-2" />
                    Import Structure (CSV/JSON)
                  </Button>
                  <Button variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Export Structure
                  </Button>
                </div>
              </div>
            </FluentCard>
          </TabsContent>

          {/* Tags Tab */}
          <TabsContent value="tags" className="space-y-6">
            <FluentCard>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600">
                  <Tags className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Tag Management</h3>
                  <p className="text-sm text-gray-500">Organize and manage content tags</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Tag Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                    <div className="space-y-0.5">
                      <Label>Auto-Suggest Tags</Label>
                      <p className="text-sm text-gray-500">Show tag suggestions while typing</p>
                    </div>
                    <Switch
                      checked={tagSettings.autoSuggestEnabled}
                      onCheckedChange={(checked) => setTagSettings(s => ({ ...s, autoSuggestEnabled: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                    <div className="space-y-0.5">
                      <Label>Allow Custom Tags</Label>
                      <p className="text-sm text-gray-500">Let users create new tags</p>
                    </div>
                    <Switch
                      checked={tagSettings.allowCustomTags}
                      onCheckedChange={(checked) => setTagSettings(s => ({ ...s, allowCustomTags: checked }))}
                    />
                  </div>
                </div>

                {/* Tag Actions */}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowMergeTagsDialog(true)}>
                    <Merge className="h-4 w-4 mr-2" />
                    Merge Tags
                  </Button>
                  <Button variant="outline" onClick={() => setShowDeleteUnusedTagsDialog(true)}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Unused
                  </Button>
                  <Button variant="outline" onClick={() => setShowCreateTagGroupDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Group
                  </Button>
                </div>

                {/* Tags List */}
                <div className="border rounded-xl">
                  <div className="p-3 bg-gray-50 dark:bg-gray-800/50 border-b font-medium text-sm grid grid-cols-4">
                    <span>Tag Name</span>
                    <span>Usage Count</span>
                    <span>Last Used</span>
                    <span>Actions</span>
                  </div>
                  <div className="divide-y dark:divide-gray-700">
                    {tagSettings.tags.map((tag) => (
                      <div key={tag.id} className="p-3 grid grid-cols-4 items-center">
                        <span className="font-medium">{tag.name}</span>
                        <span className="text-gray-500">
                          {tag.usageCount} {tag.usageCount === 0 && <Badge variant="secondary" className="ml-2">Unused</Badge>}
                        </span>
                        <span className="text-gray-500 text-sm">{tag.lastUsedAt ? new Date(tag.lastUsedAt).toLocaleDateString() : 'Never'}</span>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm">Edit</Button>
                          <Button variant="ghost" size="sm" className="text-red-500">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button onClick={handleSaveTagSettings} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </div>
            </FluentCard>
          </TabsContent>

          {/* Content Types Tab */}
          <TabsContent value="content-types" className="space-y-6">
            <FluentCard>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-green-400 to-green-600">
                  <FileType className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Content Types Configuration</h3>
                  <p className="text-sm text-gray-500">Enable and configure content types</p>
                </div>
              </div>

              <div className="space-y-4">
                {generalSettings.enabledContentTypes.map((contentType, i) => (
                  <div key={i} className="flex items-center justify-between p-4 rounded-xl border">
                    <div className="flex items-center gap-4">
                      <Switch
                        checked={contentType.enabled}
                        onCheckedChange={(checked) => {
                          setGeneralSettings(s => ({
                            ...s,
                            enabledContentTypes: s.enabledContentTypes.map((ct, idx) =>
                              idx === i ? { ...ct, enabled: checked } : ct
                            ),
                          }))
                        }}
                      />
                      <div>
                        <p className="font-medium">{contentType.type.replace(/([A-Z])/g, ' $1').trim()}</p>
                        <p className="text-sm text-gray-500">
                          Max {contentType.maxFileSizeMb}MB
                          {contentType.allowedFormats.length > 0 && ` • ${contentType.allowedFormats.join(', ')}`}
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">Configure</Button>
                  </div>
                ))}
              </div>
            </FluentCard>
          </TabsContent>

          {/* Connectors Tab */}
          <TabsContent value="connectors" className="space-y-6">
            <FluentCard>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-cyan-400 to-cyan-600">
                    <Database className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Data Connectors</h3>
                    <p className="text-sm text-gray-500">Manage external data sources for smart fields</p>
                  </div>
                </div>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Connector
                </Button>
              </div>

              <div className="space-y-4">
                {connectorSettings.connectors.map((connector) => (
                  <div key={connector.id} className="flex items-center justify-between p-4 rounded-xl border">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${
                        connector.status === 'active' ? 'bg-green-100 dark:bg-green-900/30' :
                        connector.status === 'error' ? 'bg-red-100 dark:bg-red-900/30' :
                        'bg-gray-100 dark:bg-gray-800'
                      }`}>
                        <Database className={`h-5 w-5 ${
                          connector.status === 'active' ? 'text-green-600 dark:text-green-400' :
                          connector.status === 'error' ? 'text-red-600 dark:text-red-400' :
                          'text-gray-500'
                        }`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{connector.name}</p>
                          {getConnectorStatusBadge(connector.status)}
                        </div>
                        <p className="text-sm text-gray-500">
                          {connector.type} • {connector.fieldCount} fields
                          {connector.lastSyncAt && ` • Last sync: ${formatDateTime(connector.lastSyncAt)}`}
                        </p>
                        {connector.status === 'error' && connector.lastSyncStatus && (
                          <p className="text-sm text-red-500">{connector.lastSyncStatus}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleTestConnector(connector)}>
                        <Play className="h-4 w-4 mr-1" />
                        Test
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleSyncConnector(connector)}>
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Sync
                      </Button>
                      <Button variant="ghost" size="sm">Configure</Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Sync Schedule */}
              <div className="mt-6 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Default Sync Schedule</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label>Interval:</Label>
                    <Select
                      value={String(connectorSettings.syncSchedule.defaultIntervalMinutes)}
                      onValueChange={(v) => setConnectorSettings(s => ({
                        ...s,
                        syncSchedule: { ...s.syncSchedule, defaultIntervalMinutes: Number(v) }
                      }))}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="60">1 hour</SelectItem>
                        <SelectItem value="360">6 hours</SelectItem>
                        <SelectItem value="1440">24 hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label>Max concurrent:</Label>
                    <Input
                      type="number"
                      value={connectorSettings.syncSchedule.maxConcurrentSyncs}
                      onChange={(e) => setConnectorSettings(s => ({
                        ...s,
                        syncSchedule: { ...s.syncSchedule, maxConcurrentSyncs: Number(e.target.value) }
                      }))}
                      className="w-16"
                      min={1}
                      max={10}
                    />
                  </div>
                </div>
              </div>
            </FluentCard>
          </TabsContent>

          {/* Permissions Tab */}
          <TabsContent value="permissions" className="space-y-6">
            <FluentCard>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-red-400 to-red-600">
                  <Shield className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Access Permissions</h3>
                  <p className="text-sm text-gray-500">Control who can access Content Hub features</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Create Content */}
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-base">Who can create content</Label>
                    <Button variant="outline" size="sm">
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {permissionSettings.canCreateContent.map((rule, i) => (
                      <Badge key={i} variant="secondary" className="py-1 px-3">
                        {rule.name}
                        <button className="ml-2 text-gray-400 hover:text-gray-600">×</button>
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Delete Content */}
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-base">Who can delete content</Label>
                    <Button variant="outline" size="sm">
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {permissionSettings.canDeleteContent.map((rule, i) => (
                      <Badge key={i} variant="secondary" className="py-1 px-3">
                        {rule.name}
                        <button className="ml-2 text-gray-400 hover:text-gray-600">×</button>
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Manage Folders */}
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-base">Who can manage folders</Label>
                    <Button variant="outline" size="sm">
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {permissionSettings.canManageFolders.map((rule, i) => (
                      <Badge key={i} variant="secondary" className="py-1 px-3">
                        {rule.name}
                        <button className="ml-2 text-gray-400 hover:text-gray-600">×</button>
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Approval Workflow */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                  <div className="space-y-0.5">
                    <Label className="text-base">Content Approval Workflow</Label>
                    <p className="text-sm text-gray-500">Require approval before publishing new content</p>
                  </div>
                  <Switch
                    checked={permissionSettings.approvalWorkflowEnabled}
                    onCheckedChange={(checked) => setPermissionSettings(s => ({ ...s, approvalWorkflowEnabled: checked }))}
                  />
                </div>

                <div className="flex justify-end pt-4">
                  <Button onClick={handleSavePermissionSettings} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </div>
            </FluentCard>
          </TabsContent>

          {/* Import/Export Tab */}
          <TabsContent value="import-export" className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              {/* Export Section */}
              <FluentCard>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-400 to-indigo-600">
                    <Download className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Export Content</h3>
                    <p className="text-sm text-gray-500">Download your content library</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button variant="outline" className="w-full justify-start" onClick={() => handleExport('csv')}>
                    <Download className="h-4 w-4 mr-2" />
                    Export as CSV
                  </Button>
                  <Button variant="outline" className="w-full justify-start" onClick={() => handleExport('json')}>
                    <Download className="h-4 w-4 mr-2" />
                    Export as JSON
                  </Button>
                  <Button variant="outline" className="w-full justify-start" onClick={() => handleExport('zip')}>
                    <Download className="h-4 w-4 mr-2" />
                    Export as ZIP (with files)
                  </Button>
                </div>
              </FluentCard>

              {/* Import Section */}
              <FluentCard>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600">
                    <Upload className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Import Content</h3>
                    <p className="text-sm text-gray-500">Upload content from file</p>
                  </div>
                </div>

                <div className="border-2 border-dashed rounded-xl p-8 text-center">
                  <Upload className="h-10 w-10 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600 dark:text-gray-400 mb-1">Drag and drop your file here</p>
                  <p className="text-sm text-gray-500 mb-4">Supports CSV, JSON, or ZIP</p>
                  <Button variant="outline">Choose File</Button>
                </div>
              </FluentCard>
            </div>

            {/* Backup Section */}
            <FluentCard>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-gray-400 to-gray-600">
                  <Save className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Backup & Restore</h3>
                  <p className="text-sm text-gray-500">Create and manage backups of your Content Hub</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                  <div className="space-y-0.5">
                    <Label>Automatic Backups</Label>
                    <p className="text-sm text-gray-500">
                      {importExportSettings.autoBackupEnabled
                        ? `Every ${importExportSettings.autoBackupIntervalDays} days`
                        : 'Disabled'}
                    </p>
                  </div>
                  <Switch
                    checked={importExportSettings.autoBackupEnabled}
                    onCheckedChange={(checked) => setImportExportSettings(s => ({ ...s, autoBackupEnabled: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">
                      Last backup: {importExportSettings.lastBackupAt ? formatDateTime(importExportSettings.lastBackupAt) : 'Never'}
                    </p>
                  </div>
                  <Button onClick={handleCreateBackup}>
                    <Save className="h-4 w-4 mr-2" />
                    Create Backup Now
                  </Button>
                </div>

                <div className="flex justify-end pt-4">
                  <Button onClick={handleSaveImportExportSettings} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </div>
            </FluentCard>
          </TabsContent>
        </Tabs>

        {/* Dialogs */}

        {/* Merge Tags Dialog */}
        <Dialog open={showMergeTagsDialog} onOpenChange={setShowMergeTagsDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Merge Tags</DialogTitle>
              <DialogDescription>
                Select tags to merge. All content will be updated to use the target tag.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                {tagSettings.tags.map((tag) => (
                  <label key={tag.id} className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedTags.includes(tag.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTags([...selectedTags, tag.id])
                        } else {
                          setSelectedTags(selectedTags.filter(id => id !== tag.id))
                        }
                      }}
                      className="rounded"
                    />
                    <span>{tag.name}</span>
                    <span className="text-sm text-gray-500">({tag.usageCount} uses)</span>
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMergeTagsDialog(false)}>Cancel</Button>
              <Button onClick={handleMergeTags} disabled={selectedTags.length < 2 || saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Merge className="h-4 w-4 mr-2" />}
                Merge {selectedTags.length} Tags
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Unused Tags Dialog */}
        <AlertDialog open={showDeleteUnusedTagsDialog} onOpenChange={setShowDeleteUnusedTagsDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Unused Tags</AlertDialogTitle>
              <AlertDialogDescription>
                This will delete {tagSettings.tags.filter(t => t.usageCount === 0).length} tags that are not used by any content.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteUnusedTags} className="bg-red-600 hover:bg-red-700">
                Delete Unused Tags
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Create Tag Group Dialog */}
        <Dialog open={showCreateTagGroupDialog} onOpenChange={setShowCreateTagGroupDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Tag Group</DialogTitle>
              <DialogDescription>
                Tag groups help organize related tags together.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Group Name</Label>
                <Input
                  value={newTagGroupName}
                  onChange={(e) => setNewTagGroupName(e.target.value)}
                  placeholder="Enter group name"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateTagGroupDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateTagGroup} disabled={!newTagGroupName.trim() || saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Create Group
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Test Connector Dialog */}
        <Dialog open={showTestConnectorDialog} onOpenChange={setShowTestConnectorDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Test Connection</DialogTitle>
              <DialogDescription>
                Testing connection to {selectedConnector?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="py-8 flex flex-col items-center justify-center">
              {testingConnector ? (
                <>
                  <Loader2 className="h-12 w-12 animate-spin text-blue-500 mb-4" />
                  <p className="text-gray-500">Testing connection...</p>
                </>
              ) : testResult ? (
                <>
                  {testResult.success ? (
                    <CheckCircle2 className="h-12 w-12 text-green-500 mb-4" />
                  ) : (
                    <XCircle className="h-12 w-12 text-red-500 mb-4" />
                  )}
                  <p className={testResult.success ? 'text-green-600' : 'text-red-600'}>
                    {testResult.message}
                  </p>
                </>
              ) : null}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTestConnectorDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      )}
    </PermissionGate>
  )
}
